import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography, CircularProgress, Alert, Button } from '@mui/joy';
import { useSupabaseAuth } from '../contexts/SupabaseAuthContext';
import { useProfile } from '../hooks/useProfile';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
  requireStaff?: boolean;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requireAdmin = false,
  requireStaff = false 
}) => {
  const { user, loading: authLoading } = useSupabaseAuth();
  const { profile, loading: profileLoading, canAccessApp, canAccessAdmin, isStaff, isSiteCrew, isSupplier } = useProfile();
  const navigate = useNavigate();
  const location = useLocation();

  const loading = authLoading || profileLoading;

  React.useEffect(() => {
    if (!loading && !user) {
      navigate('/login', { 
        replace: true,
        state: { from: location.pathname }
      });
      return;
    }

    if (!loading && user && profile) {
      if (!canAccessApp) {
        navigate('/access-denied', { 
          replace: true,
          state: { 
            from: location.pathname,
            reason: 'not-staff'
          }
        });
        return;
      }

      if (requireAdmin && !canAccessAdmin) {
        navigate('/access-denied', { 
          replace: true,
          state: { 
            from: location.pathname,
            reason: 'not-admin'
          }
        });
        return;
      }

      // If role is 'document', restrict access to documents pages.
      if (isStaff && profile.role === 'document') {
        const isAllowed = location.pathname === '/docs' || location.pathname.startsWith('/docs/');
        if (!isAllowed) {
          navigate('/access-denied', {
            replace: true,
            state: {
              from: location.pathname,
              reason: 'document-role-restricted'
            }
          });
          return;
        }
      }

      if (isSiteCrew) {
        const invoiceMatch = location.pathname.match(/^\/job-records\/([^/]+)\/[^/]+$/);
        if (invoiceMatch && invoiceMatch[1] !== 'review') {
          navigate(`/job-records/${invoiceMatch[1]}`, { replace: true });
          return;
        }
        const allowed = location.pathname === '/job-records'
          || location.pathname === '/cis-certificates'
          || location.pathname === '/fleet-policy'
          || (/^\/job-records\/[^/]+$/.test(location.pathname) && location.pathname !== '/job-records/review');
        if (!allowed) {
          navigate('/job-records', { replace: true });
          return;
        }
      }

      if (isSupplier && !location.pathname.startsWith('/supplier-portal')) {
        navigate('/supplier-portal', { replace: true });
        return;
      }

      if (requireStaff && !isStaff) {
        navigate('/access-denied', { 
          replace: true,
          state: { 
            from: location.pathname,
            reason: 'not-staff'
          }
        });
        return;
      }
    }
  }, [user, loading, profile, canAccessApp, canAccessAdmin, isStaff, isSiteCrew, isSupplier, requireAdmin, requireStaff, navigate, location]);

  if (loading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          gap: 2
        }}
      >
        <CircularProgress size="lg" />
        <Typography level="body-lg" color="neutral">
          Loading...
        </Typography>
      </Box>
    );
  }

  if (!user) {
    return null; // Will redirect to login
  }

  if (!profile) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          gap: 2
        }}
      >
        <CircularProgress size="lg" />
        <Typography level="body-lg" color="neutral">
          Loading profile...
        </Typography>
      </Box>
    );
  }

  // Check access permissions
  if (!canAccessApp) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          gap: 2,
          p: 3
        }}
      >
        <Alert color="danger" sx={{ mb: 2 }}>
          Access Denied
        </Alert>
        <Typography level="h4" mb={2}>
          Access Restricted
        </Typography>
        <Typography level="body-lg" textAlign="center" mb={3}>
          You do not have permission to access this application. 
          Only staff members can access the Jarvis application.
        </Typography>
        <Button 
          variant="outlined" 
          onClick={() => navigate('/login')}
        >
          Back to Login
        </Button>
      </Box>
    );
  }

  if (requireAdmin && !canAccessAdmin) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          gap: 2,
          p: 3
        }}
      >
        <Alert color="danger" sx={{ mb: 2 }}>
          Admin Access Required
        </Alert>
        <Typography level="h4" mb={2}>
          Admin Access Required
        </Typography>
        <Typography level="body-lg" textAlign="center" mb={3}>
          You need administrator privileges to access this page.
        </Typography>
        <Button 
          variant="outlined" 
          onClick={() => navigate('/workspaces')}
        >
          Back to Dashboard
        </Button>
      </Box>
    );
  }

  if (requireStaff && !isStaff) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          gap: 2,
          p: 3
        }}
      >
        <Alert color="danger" sx={{ mb: 2 }}>
          Staff Access Required
        </Alert>
        <Typography level="h4" mb={2}>
          Staff Access Required
        </Typography>
        <Typography level="body-lg" textAlign="center" mb={3}>
          You need staff privileges to access this page.
        </Typography>
        <Button 
          variant="outlined" 
          onClick={() => navigate('/workspaces')}
        >
          Back to Dashboard
        </Button>
      </Box>
    );
  }

  return <>{children}</>;
}; 